import { useCallback, useEffect, useRef, useState } from "react";
import { clampDockPosition, HELP_VIDEOS_DOCK_POSITION_KEY, loadDockPosition, saveDockPosition, type DockPosition } from "./dockPosition";

/** Movement beyond this counts as a drag rather than a click. */
const DRAG_THRESHOLD_PX = 4;

/**
 * Makes a floating dock draggable, and remembers where it was put.
 *
 * Ported from web-admin's `useDraggableDock` (the feedback dock). The dock is fixed over the app, so
 * wherever it sits by default it will eventually cover a control somebody needs. Rather than guess a
 * better corner, it can be moved — and the position persists, because re-dragging it on every page
 * would be worse than the original problem.
 *
 * Two things this has to get right, both learned the hard way in the product this is ported from:
 *
 * - **A drag must not also be a click.** A collapsed pill or header is both a toggle/handle and its
 *   own drag surface, so movement past a small threshold has to suppress the click that would
 *   otherwise follow.
 * - **It can never be lost.** The position is clamped on drop, on restore, and on resize. Without the
 *   restore clamp, a position saved at the edge of a wide monitor puts the dock off a laptop's
 *   viewport with no way back — the clamp lives in `dockPosition.ts` and is unit-tested there.
 */
export function useDraggableDock(positionKey: string = HELP_VIDEOS_DOCK_POSITION_KEY) {
  const elementRef = useRef<HTMLDivElement | null>(null);

  const [position, setPosition] = useState<DockPosition | null>(() => {
    const stored = loadDockPosition(positionKey);
    if (!stored) return null;
    // Clamped in the initialiser so it costs no render and needs no effect.
    return clampDockPosition(stored, { width: window.innerWidth, height: window.innerHeight });
  });
  const [dragging, setDragging] = useState(false);

  const originRef = useRef<{ pointerX: number; pointerY: number; x: number; y: number } | null>(null);
  const movedRef = useRef(false);

  const clamp = useCallback((next: DockPosition): DockPosition => {
    const width = elementRef.current?.getBoundingClientRect().width ?? 0;
    return clampDockPosition(next, { width: window.innerWidth, height: window.innerHeight }, width);
  }, []);

  // A window that shrinks after the dock was dragged near an edge would otherwise strand it.
  useEffect(() => {
    const onResize = () => {
      setPosition((current) => {
        if (!current) return current;
        const clamped = clamp(current);
        if (clamped.x === current.x && clamped.y === current.y) return current;
        saveDockPosition(clamped, positionKey);
        return clamped;
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [clamp, positionKey]);

  const onPointerDown = useCallback((event: React.PointerEvent) => {
    if (event.button !== 0) return; // left button / touch only

    // Never start a drag from a control INSIDE the handle. Capturing the pointer retargets the
    // pointerup to the handle, so the control's own click would never fire — which is what stopped
    // the collapse button working, since it lives in the header that is the handle.
    // The collapsed pill IS its own handle, so a control that *is* currentTarget still drags.
    const control = (event.target as HTMLElement | null)?.closest('button, a, input, textarea, select, [role="button"]');
    if (control && control !== event.currentTarget) return;

    const rect = elementRef.current?.getBoundingClientRect();
    if (!rect) return;
    originRef.current = { pointerX: event.clientX, pointerY: event.clientY, x: rect.left, y: rect.top };
    movedRef.current = false;
    event.currentTarget.setPointerCapture(event.pointerId);
  }, []);

  const onPointerMove = useCallback(
    (event: React.PointerEvent) => {
      const origin = originRef.current;
      if (!origin) return;
      const dx = event.clientX - origin.pointerX;
      const dy = event.clientY - origin.pointerY;
      if (!movedRef.current && Math.abs(dx) < DRAG_THRESHOLD_PX && Math.abs(dy) < DRAG_THRESHOLD_PX) return;
      if (!movedRef.current) {
        movedRef.current = true;
        setDragging(true);
      }
      setPosition(clamp({ x: origin.x + dx, y: origin.y + dy }));
    },
    [clamp],
  );

  const onPointerUp = useCallback((event: React.PointerEvent) => {
    if (!originRef.current) return;
    originRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    if (movedRef.current) {
      setDragging(false);
      setPosition((current) => {
        if (current) saveDockPosition(current, positionKey);
        return current;
      });
    }
  }, [positionKey]);

  /** Swallows the click that follows a drag, so letting go over the pill doesn't also toggle it. */
  const suppressClickAfterDrag = useCallback(() => {
    const wasDrag = movedRef.current;
    movedRef.current = false;
    return wasDrag;
  }, []);

  return {
    position,
    dragging,
    elementRef,
    suppressClickAfterDrag,
    handleProps: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp },
  };
}
